import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import './SearchableDropdown.css';

interface SearchableDropdownProps {
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
  searchFunction: (query: string) => Promise<{ id: number; name: string }[]>;
  required?: boolean;
  label: string;
}

const SearchableDropdown: React.FC<SearchableDropdownProps> = ({
  value,
  onChange,
  placeholder,
  searchFunction,
  required = false,
  label
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState(value);
  const [options, setOptions] = useState<{ id: number; name: string }[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 });

  const dropdownRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Load initial options when component mounts
  useEffect(() => {
    if (isOpen && options.length === 0) {
      loadOptions('');
    }
  }, [isOpen]);

  // Update search query when value changes externally
  useEffect(() => {
    setSearchQuery(value);
  }, [value]);

  // Close dropdown when clicking outside and handle position updates
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      const isInsideContainer = containerRef.current && containerRef.current.contains(target);
      const isInsideDropdown = dropdownRef.current && dropdownRef.current.contains(target);

      if (!isInsideContainer && !isInsideDropdown) {
        setIsOpen(false);
        setSelectedIndex(-1);
      }
    };

    const updateDropdownPosition = () => {
      if (containerRef.current && isOpen) {
        const rect = containerRef.current.getBoundingClientRect();
        setDropdownPosition({
          top: rect.bottom + window.scrollY,
          left: rect.left + window.scrollX,
          width: rect.width
        });
      }
    };

    const handleResize = () => updateDropdownPosition();
    const handleScroll = () => updateDropdownPosition();

    document.addEventListener('mousedown', handleClickOutside);
    window.addEventListener('resize', handleResize);
    window.addEventListener('scroll', handleScroll);

    if (isOpen) {
      updateDropdownPosition();
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('scroll', handleScroll);
    };
  }, [isOpen]);

  const loadOptions = async (query: string) => {
    setLoading(true);
    try {
      const results = await searchFunction(query);
      setOptions(results);
    } catch (error) {
      console.error('Error loading options:', error);
      setOptions([]);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newQuery = e.target.value;
    setSearchQuery(newQuery);
    setSelectedIndex(-1);

    if (newQuery.trim()) {
      loadOptions(newQuery);
    } else {
      loadOptions('');
    }

    if (!isOpen) {
      setIsOpen(true);
    }
  };

  const handleOptionClick = (option: { id: number; name: string }) => {
    setSearchQuery(option.name);
    onChange(option.name);
    setIsOpen(false);
    setSelectedIndex(-1);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!isOpen) {
      if (e.key === 'ArrowDown' || e.key === 'Enter') {
        setIsOpen(true);
        if (options.length === 0) {
          loadOptions(searchQuery);
        }
      }
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev =>
          prev < options.length - 1 ? prev + 1 : 0
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev =>
          prev > 0 ? prev - 1 : options.length - 1
        );
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedIndex >= 0 && options[selectedIndex]) {
          handleOptionClick(options[selectedIndex]);
        }
        break;
      case 'Escape':
        setIsOpen(false);
        setSelectedIndex(-1);
        inputRef.current?.blur();
        break;
    }
  };

  const handleInputFocus = () => {
    setIsOpen(true);
    if (options.length === 0) {
      loadOptions(searchQuery);
    }
  };

  const renderDropdown = () => {
    if (!isOpen) return null;

    const dropdown = (
      <div
        ref={dropdownRef}
        className="searchable-dropdown-menu searchable-dropdown-portal"
        style={{
          position: 'absolute',
          top: dropdownPosition.top,
          left: dropdownPosition.left,
          width: dropdownPosition.width,
          zIndex: 2147483647
        }}
      >
        {loading ? (
          <div className="dropdown-loading">Loading...</div>
        ) : options.length === 0 ? (
          <div className="dropdown-no-results">
            {searchQuery.trim() ? 'No results found' : 'Start typing to search...'}
          </div>
        ) : (
          options.map((option, index) => (
            <div
              key={option.id}
              className={`dropdown-option ${index === selectedIndex ? 'selected' : ''}`}
              onClick={() => handleOptionClick(option)}
              onMouseEnter={() => setSelectedIndex(index)}
            >
              {option.name}
            </div>
          ))
        )}
      </div>
    );

    return createPortal(dropdown, document.body);
  };

  return (
    <div className="form-group">
      <label>{label} {required && '*'}</label>
      <div className="searchable-dropdown" ref={containerRef}>
        <input
          ref={inputRef}
          type="text"
          value={searchQuery}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          required={required}
          autoComplete="off"
          className={`searchable-dropdown-input ${isOpen ? 'open' : ''}`}
        />

        {renderDropdown()}
      </div>
    </div>
  );
};

export default SearchableDropdown;
